import type {ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import classNames from 'classnames'
import {Manager, Popper, Reference} from 'react-popper'
import {AnimatePresence, motion} from 'framer-motion'
import type {ArrowPlacement} from './Arrow'
import Arrow from './Arrow'
import {Portal} from 'react-portal'
import type {CommonProps} from '../@types/common'
import type {State as PopperJsState} from '@popperjs/core'
import {MdClose} from "react-icons/md";

export interface TooltipProps extends CommonProps {
    isOpen?: boolean
    setOpen?: (arg0: boolean) => void
    placement?: ArrowPlacement
    title: string | ReactNode
    wrapperClass?: string
    showClose?: boolean
    persist?: boolean
}

const PopperElement = (props: {
    title: string | ReactNode
    open: boolean
    forceUpdate: () => Partial<PopperJsState>,
    setOpen: (arg0: boolean) => void
    showClose?: boolean
}) => {
    const {title, forceUpdate, open, setOpen, showClose} = props

    useEffect(() => {
        if (open) {
            const timeout = setTimeout(() => forceUpdate(), 0);
            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])
    return <div>
        {showClose && <span onClick={() => setOpen(false)}><MdClose/></span>}
        {title}
    </div>
}

const Tooltip = (props: TooltipProps) => {
    const {
        className,
        children,
        placement = 'top',
        title,
        wrapperClass,
        showClose,
        persist,
        ...rest
    } = props

    const {isOpen, setOpen} = props
    const [open, sOpen] = useState(persist)
    const tooltipOpen = useMemo(() => isOpen || open, [isOpen, open])
    const setTooltipOpen = (open: boolean) => {
        // if (persist) return
        sOpen(open)
        setOpen?.(open)
    }

    // const [tooltipOpen, setTooltipOpen] = useState<boolean>(isOpen)
    const tooltipNode = useRef()

    const tooltipBackground = 'gray-800'
    const tooltipDarkBackground = 'black'
    const arrowRef = useRef(null)

    const defaultTooltipClass = `tooltip bg-${tooltipBackground} dark:bg-${tooltipDarkBackground}`

    const toggleTooltip = useCallback(
        (bool: boolean) => {
            if (persist) return
            if (!isOpen) {
                setTooltipOpen(bool)
            }
        },
        [isOpen]
    )

    if (!title?.length)
        return children

    return (
        <Manager>
            <Reference>
                {({ref}) => <span
                    ref={ref}
                    className={classNames('tooltip-wrapper', wrapperClass)}
                    onMouseEnter={() => toggleTooltip(true)}
                    onMouseLeave={() => toggleTooltip(false)}
                >
                        {children}
                    </span>
                }
            </Reference>
            {tooltipOpen && (
                <Portal>
                    <Popper
                        placement={placement}
                        innerRef={(node) => (tooltipNode.current = node)}
                        modifiers={[
                            {
                                name: 'arrow',
                                options: {
                                    element: arrowRef.current,
                                },
                            },
                            // {
                            //     name: 'arrow',
                            //     options: {
                            //         element: Arrow as unknown as HTMLElement,
                            //     },
                            // },
                            {name: 'offset', options: {offset: [0, 7]}},
                        ]}
                        strategy={'fixed'}
                    >
                        {({ref, style, ...popperProps}) => (
                            <AnimatePresence>
                                <motion.div
                                    ref={ref}
                                    className={classNames(
                                        defaultTooltipClass,
                                        className
                                    )}
                                    style={style}
                                    initial={{
                                        opacity: 0,
                                        visibility: 'hidden',
                                    }}
                                    animate={
                                        tooltipOpen
                                            ? {
                                                opacity: 1,
                                                visibility: 'visible',
                                            }
                                            : {
                                                opacity: 0,
                                                visibility: 'hidden',
                                            }
                                    }
                                    exit={{
                                        opacity: 0,
                                        visibility: 'hidden',
                                    }}
                                    transition={{
                                        duration: 0.15,
                                        type: 'tween',
                                    }}
                                >
                                    <PopperElement
                                        open={tooltipOpen}
                                        title={title}
                                        setOpen={setTooltipOpen}
                                        showClose={showClose}
                                        {...rest}
                                        {...popperProps}
                                    />
                                    <Arrow
                                        ref={arrowRef}
                                        placement={placement}
                                        color={tooltipBackground}
                                        colorDark={tooltipDarkBackground}
                                    />
                                </motion.div>
                            </AnimatePresence>
                        )}
                    </Popper>
                </Portal>
            )}
        </Manager>
    )
}

Tooltip.displayName = 'Tooltip'

export default Tooltip
